import { useCallback, useMemo } from "react";
import {
  MenuItem,
  MenuSeparator,
  DataTable,
  DataTableCellContent,
  DataTableCellTrigger,
  DataTableEmpty,
  createColumnHelper,
  renderDefaultGroupActions,
  Badge,
  Box,
  Text,
  Icon,
  type DataTableColumnDef,
  type EntityItemConfig,
  type BulkAction,
  type DataTableGroupType,
  type OnChangeFn,
  type SortingState,
} from "@salestrak/ui";

import { EntityTag } from "#app/components/entity";
import { ENTITY_KIND_ICON } from "#app/lib/entity-visuals";
import { type FilterDef } from "#app/lib/hooks/use-table-filters";
import type { TableViewTableProps } from "#app/lib/hooks/use-table-view-settings";
import { type TeamRow, TeamsListDataDocument } from "#app/lib/data";

export function buildTeamsFilterDefs(
  memberPoolItems: EntityItemConfig[],
): FilterDef<EntityItemConfig>[] {
  const pools = memberPoolItems.length > 0
    ? [{ id: "users", label: "Members", items: memberPoolItems }]
    : [];
  return [
    { key: "leadIds", label: "Leads", icon: "user", type: "entity", pools, placeholder: "Search leads...", filterFormat: "idArray" },
    { key: "memberIds", label: "Teammates", icon: ENTITY_KIND_ICON.team, type: "entity", pools, placeholder: "Search teammates...", filterFormat: "idArray" },
  ];
}
import {
  GroupQueryProvider,
  useGroupStates,
} from "#app/lib/hooks/group-query-provider";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TeamsTableVariant = "app" | "settings";

/**
 * Shared by the table (sticky pinning), the column settings panel (locked
 * rows), and both parent pages. The panel ignores structural ids like
 * "select".
 */
export const TEAMS_PINNED_COLUMN_IDS = ["select", "teamName"];

type TeamsTableProps = Omittable<{
  where: Record<string, unknown>;
  // Undefined when the list defers ordering to the API (`deferOrderBy`) and the
  // user has not sorted — the variable is then simply omitted.
  orderBy: Record<string, string> | undefined;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  searchValue: string;
  variant?: TeamsTableVariant;
  /**
   * Column definitions from `useTeamsColumns` — built by the parent page so
   * the `DataTableColumnSettings` panel in its header shares the same defs.
   */
  columns: DataTableColumnDef<TeamRow>[];
  /** Per-user order/hidden/width customization from `useTableViewSettings`. */
  tableViewProps?: TableViewTableProps;
  memberPoolItems?: EntityItemConfig[];
  // App variant
  onViewTeam?: (teamId: string) => void;
  // Settings variant
  onNavigateToTeam?: (teamId: string) => void;
  onNavigateToTeamMembers?: (teamId: string) => void;
  onDeleteTeam?: (teamId: string, teamName: string) => void;
  onLeaveTeam?: (teamId: string) => void;
  // Bulk actions (settings variant)
  bulkActions?: BulkAction<TeamRow>[];
}>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getMemberCount(team: TeamRow): number {
  return team.leadCount + team.memberCount;
}

const h = createColumnHelper<TeamRow>();

// ---------------------------------------------------------------------------
// Per-group query helpers
// ---------------------------------------------------------------------------

const getConnection = (data: any) => data?.teams;

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

type UseTeamsColumnsParams = Omittable<{
  variant: TeamsTableVariant;
  onViewTeam?: (teamId: string) => void;
  onNavigateToTeam?: (teamId: string) => void;
  onNavigateToTeamMembers?: (teamId: string) => void;
}>;

export function useTeamsColumns({
  variant,
  onViewTeam,
  onNavigateToTeam,
  onNavigateToTeamMembers,
}: UseTeamsColumnsParams): DataTableColumnDef<TeamRow>[] {
  return useMemo<DataTableColumnDef<TeamRow>[]>(
    () => {
      const handler = variant === "app" ? onViewTeam : onNavigateToTeam;

      const nameColumn = h.hoverAction({
        accessorKey: "teamName",
        header: "Name",
        enableSorting: true,
        content: (team) => (
          <Box direction="row" align="center" gap="xs">
            <EntityTag
              entity={{ kind: "team", id: team.teamId, team }}
              padded
              interactive={false}
            />
            <Text size="sm" variant="muted">·</Text>
            <Text size="sm" variant="muted">{team.teamKey}</Text>
          </Box>
        ),
        onClickAction: (team) => handler?.(team.teamId),
        actionLabel: (team) => `View ${team.teamName}`,
      });

      const accessColumn = h.display({
        id: "access",
        header: "Access",
        meta: { minWidth: 120 },
        cell: ({ row }) => {
          const team = row.original;
          const isLead = team.viewerRole === "lead";
          const isMember = team.viewerRole === "member";

          return (
            <DataTableCellContent>
              <Box direction="row" align="center" gap="xs">
                {isLead ? (
                  <Badge variant="success">Lead</Badge>
                ) : isMember ? (
                  <Badge variant="success">Joined</Badge>
                ) : null}
              </Box>
            </DataTableCellContent>
          );
        },
      });

      const membersColumn = h.display({
        id: "members",
        header: "Teammates",
        meta: { minWidth: 100 },
        cell: ({ row }) => {
          const count = getMemberCount(row.original);
          const content = (
            <Box direction="row" align="center" gap="xs">
              <Icon name="users" size="sm" color="muted" />
              <Text size="sm" variant="muted">{count}</Text>
            </Box>
          );

          if (variant === "settings" && onNavigateToTeamMembers) {
            return (
              <DataTableCellTrigger onClick={() => onNavigateToTeamMembers(row.original.teamId)}>
                <DataTableCellContent>
                  {content}
                </DataTableCellContent>
              </DataTableCellTrigger>
            );
          }

          return (
            <DataTableCellContent>
              {content}
            </DataTableCellContent>
          );
        },
      });

      const createdColumn = h.date({ accessorKey: "creationDate", header: "Created" });
      const updatedColumn = h.date({ accessorKey: "lastUpdateDate", header: "Updated" });

      if (variant === "app") {
        return [
          nameColumn,
          accessColumn,
          membersColumn,
          createdColumn,
          updatedColumn,
        ];
      }

      // Settings variant
      return [
        nameColumn,
        membersColumn,
        createdColumn,
      ];
    },
    [
      variant,
      onViewTeam,
      onNavigateToTeam,
      onNavigateToTeamMembers,
    ]
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TeamsTable({
  where: whereProp,
  orderBy: orderByProp,
  sorting,
  onSortingChange,
  searchValue,
  variant = "app",
  columns,
  tableViewProps,
  onViewTeam,
  onNavigateToTeam,
  onNavigateToTeamMembers,
  onDeleteTeam,
  onLeaveTeam,
  bulkActions,
}: TeamsTableProps) {
  // --- Group query state ---
  const { states, registerState, getGroupProps } = useGroupStates<TeamRow>();

  const handleRenderRowActions = useCallback(
    (team: TeamRow) => {
      if (variant === "app") {
        const isLead = team.viewerRole === "lead";
        const isMember = team.viewerRole === "member";
        const isOnTeam = isLead || isMember;
        const isLastLead = isLead && team.leadCount === 1;

        return (
          <>
            <MenuItem onClick={() => onViewTeam?.(team.teamId)}>
              <Icon name={ENTITY_KIND_ICON.team} size="lg" color="muted" />
              <Text size="sm">View team</Text>
            </MenuItem>
            {onNavigateToTeam ? (
              <MenuItem onClick={() => onNavigateToTeam(team.teamId)}>
                <Icon name="settings" size="lg" color="muted" />
                <Text size="sm">Team settings</Text>
              </MenuItem>
            ) : null}
            {isOnTeam ? (
              <>
                <MenuSeparator />
                <MenuItem
                  onClick={() => onLeaveTeam?.(team.teamId)}
                  disabled={isLastLead}
                >
                  <Icon name="log-out" size="lg" color="destructive" />
                  <Text size="sm" variant="destructive">Leave team…</Text>
                </MenuItem>
              </>
            ) : null}
          </>
        );
      }

      // Settings variant
      return (
        <>
          <MenuItem onClick={() => onNavigateToTeam?.(team.teamId)}>
            <Icon name="settings" size="lg" color="muted" />
            <Text size="sm">General</Text>
          </MenuItem>
          <MenuItem
            onClick={() => onNavigateToTeamMembers?.(team.teamId)}
          >
            <Icon name="users" size="lg" color="muted" />
            <Text size="sm">Teammates</Text>
          </MenuItem>
          <MenuSeparator />
          <MenuItem onClick={() => onDeleteTeam?.(team.teamId, team.teamName)}>
            <Icon name="trash-2" size="lg" color="destructive" />
            <Text size="sm" variant="destructive">Delete team</Text>
          </MenuItem>
        </>
      );
    },
    [variant, onViewTeam, onLeaveTeam, onNavigateToTeam, onNavigateToTeamMembers, onDeleteTeam],
  );

  const allProps = getGroupProps("__all__");
  const allTotal = states.get("__all__")?.total;

  const groups = useMemo<DataTableGroupType<TeamRow>[]>(
    () => [
      {
        key: "__all__",
        label: "All Teams",
        totalCount: allTotal,
        ...allProps,
      },
    ],
    [allProps, allTotal],
  );

  return (
    <>
      {/* Ungrouped query provider */}
      <GroupQueryProvider<TeamRow>
        groupKey="__all__"
        document={TeamsListDataDocument}
        variables={{ where: whereProp, orderBy: orderByProp, limit: 50 }}
        getConnection={getConnection}
        onStateChange={registerState}
      />

      <Box direction="column" fill="flex">
        <DataTable
          columns={columns}
          groups={groups}
          getRowId={(r) => r.teamId}
          sorting={sorting}
          onSortingChange={onSortingChange}
          stickyHeader
          density="regular"
          pinnedColumnIds={TEAMS_PINNED_COLUMN_IDS}
          fillColumnId="teamName"
          paddingX="lg"
          {...tableViewProps}
          groupActions={renderDefaultGroupActions}
          renderRowActions={handleRenderRowActions}
          bulkActions={variant === "settings" ? bulkActions : undefined}
          emptyState={
            searchValue ? (
              <DataTableEmpty message="No teams match your search" />
            ) : (
              <DataTableEmpty
                title="No teams yet"
                description="Create teams to organize members and keep work coordinated."
                media={<Icon name="users" size="2xl" color="muted" />}
              />
            )
          }
        />
      </Box>
    </>
  );
}
